import { IconButton, Tooltip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  AssignmentTurnedInRounded,
  AutorenewRounded,
  EditRounded
} from '@material-ui/icons';
import moment from 'moment';
import { Link as RRLink } from 'react-router-dom';
import { WorkflowStatusLabel } from '../pages/ProjectDocumentRevision/DocumentRevisionView';
import { DocumentWorkflowWithStepAndApproval } from '../pages/ProjectDocumentWorkflow/projectDocumentWorkflowSlice';
import StatusChip from './StatusChip';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      [theme.breakpoints.up('sm')]: {
        alignItems: 'center'
      },
      padding: theme.spacing(1, 0),
      '&:first-child': {
        paddingTop: 0
      },
      '&:last-child': {
        paddingBottom: 0,
        borderBottomStyle: 'none'
      },
      borderBottomStyle: 'solid',
      borderWidth: 'thin',
      borderColor: '#dddddd'
    },
    actionArea: {
      flexGrow: 1,
      display: 'flex',
      [theme.breakpoints.down('xs')]: {
        display: 'block'
      },
      textDecoration: 'none',
      color: theme.palette.text.primary,
      transition: '0.4s',
      '&:hover, &.Mui-focusVisible': {
        color: theme.palette.primary.main
      }
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    },
    flowHistory: {
      flexGrow: 1
    },
    flowStatus: {
      width: '40%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        textAlign: 'right'
      }
    },
    actionIcon: {
      width: 30,
      textAlign: 'right'
    }
  })
);

interface DocumentFlowProps {
  documentWorkflows: DocumentWorkflowWithStepAndApproval[];
  isDocCreator?: boolean;
  planIssueDate: Date;
}

export default function DocumentFlow({
  documentWorkflows,
  isDocCreator = false,
  planIssueDate
}: DocumentFlowProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const isInProgressWorkflow = documentWorkflows.some(
    (documentWorkflow) =>
      documentWorkflow.workflowStatusId === WorkflowStatusLabel.InProgress
  );
  const isCompletedWorkflow = documentWorkflows.some(
    (documentWorkflow) =>
      documentWorkflow.workflowStatusId === WorkflowStatusLabel.Completed
  );

  return (
    <div>
      {documentWorkflows.map((documentWorkflow) => {
        return (
          <div className={classes.container} key={documentWorkflow.id}>
            <RRLink
              className={classes.actionArea}
              to={`/approvals/${documentWorkflow.id}`}
            >
              <div className={classes.flowHistory}>
                <div className={classes.flexAlignCenter}>
                  <Typography variant="body1">
                    {documentWorkflow.name}
                  </Typography>
                  {documentWorkflow.isAutoApproval && (
                    <Tooltip title={t('documentFlow.autoApprovalWorkflow') as string} arrow>
                      <AssignmentTurnedInRounded
                        fontSize="small"
                        color="action"
                      />
                    </Tooltip>
                  )}
                </div>
                <Typography variant="subtitle2" color="textSecondary">
                  {t('documentFlow.start')}: {moment(documentWorkflow.createdAt).format('lll')}
                </Typography>
              </div>
              <div className={classes.flowStatus}>
                <StatusChip
                  workflowWithStep={documentWorkflow}
                  planIssueDate={planIssueDate}
                />
              </div>
            </RRLink>
            <div className={classes.actionIcon}>
              {isDocCreator &&
                documentWorkflow.workflowStatusId ===
                  WorkflowStatusLabel.InProgress && (
                  <Tooltip title={t('documentFlow.editDocumentWorkflow') as string} arrow>
                    <IconButton
                      size="small"
                      component={RRLink}
                      to={`/document-workflows/${documentWorkflow.id}/edit`}
                    >
                      <EditRounded color="primary" />
                    </IconButton>
                  </Tooltip>
                )}
              {isDocCreator &&
                !isInProgressWorkflow &&
                documentWorkflow.workflowStatusId !==
                  WorkflowStatusLabel.InProgress && (
                  <Tooltip
                    title={
                      isCompletedWorkflow
                        ? t('documentFlow.cannotRestartWorkflow') as string
                        : t('documentFlow.restartWorkflow') as string
                    }
                    arrow
                  >
                    <span>
                      <IconButton
                        size="small"
                        component={RRLink}
                        to={`/document-workflows/create?documentRevisionId=${documentWorkflow.projectDocumentRevisionId}&refFlowId=${documentWorkflow.id}`}
                        disabled={isCompletedWorkflow}
                      >
                        <AutorenewRounded />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
